import { useAppStore } from '@store'
import type { Store } from '@types'

export type PersistenceStatus = Store['persistenceStatus']
export type ProjectPendingIntent = NonNullable<Store['pendingIntent']>

export function hasPersistedIdentity(
  state: Pick<Store, 'clientInfo'> = useAppStore.getState(),
): boolean {
  return (state.clientInfo.idVersiones ?? 0) > 0
}

/** Presupuesto solo si esta sesión tiene un save confirmado y hay identidad en servidor. */
export function canGeneratePresupuesto(
  state: Pick<Store, 'clientInfo' | 'persistenceStatus'> = useAppStore.getState(),
): boolean {
  return state.persistenceStatus === 'saved' && hasPersistedIdentity(state)
}

export function requestSaveWorkflow(intent: ProjectPendingIntent) {
  const store = useAppStore.getState()
  store.setPendingIntent(intent)
  sessionStorage.setItem('pendingSave', 'true')

  if (intent === 'images') {
    localStorage.setItem('pendingAction', 'openProjectImagesAfterSave')
    localStorage.setItem('pendingFrom', 'enviarImagenes')
    return
  }
  if (intent === 'newDesign') {
    localStorage.setItem('pendingAction', 'newDesignAfterSave')
    localStorage.setItem('pendingFrom', 'newDesign')
    return
  }
  if (intent === 'openOther') {
    localStorage.setItem('pendingAction', 'openProjectAfterSave')
    localStorage.setItem('pendingFrom', 'openProject')
    return
  }

  localStorage.setItem('pendingAction', 'saveProject')
  localStorage.setItem('pendingFrom', 'guardar')
}

export function isExistingDesignsRoute(route?: string) {
  return route === '/proyectos' || route === '/planner/proyectos'
}

/** Tras login al planner: hay que pasar por guardar. Conserva la ruta original para al cerrar. */
export function prepareSaveAfterLogin(nextRoute?: string) {
  const store = useAppStore.getState()
  store.requireSaveForSession()
  if (nextRoute && nextRoute !== '/planner/saveProject') {
    sessionStorage.setItem('postLoginRoute', nextRoute)
  }
  if (!store.pendingIntent) {
    requestSaveWorkflow('save')
  } else {
    sessionStorage.setItem('pendingSave', 'true')
  }
}

export function resolveRouteAfterLogin(href?: string) {
  const next = href || '/planner'
  if (isExistingDesignsRoute(next)) {
    useAppStore.getState().requireSaveForSession()
    sessionStorage.removeItem('postLoginRoute')
    return next
  }
  prepareSaveAfterLogin(next)
  return '/planner/saveProject'
}

/** Nuevo diseño: escena en blanco, sin identidad persistida, y el modal de guardar. */
export function startFreshUnsavedDesign() {
  const store = useAppStore.getState()
  store.resetStore()
  sessionStorage.removeItem('postLoginRoute')
  requestSaveWorkflow('save')
}

export function consumePostLoginRoute(): string {
  const route = sessionStorage.getItem('postLoginRoute')
  sessionStorage.removeItem('postLoginRoute')
  if (route && route !== '/planner/saveProject') return route
  return '/planner'
}
